/*
  cf-calc-analytics-review.js - Click Fencing calculator funnel, PART TWO.
  Claude, 2026-09-01 (later session).

  WHY THIS FILE EXISTS.

  assets/cf-calc-analytics.js defines a ten step ladder:

      view 1  start 2  length_entered 3  section_added 4  gate_added 5
      price_shown 6  quote_saved 7  details_opened 8  enquiry_submitted 9
      add_to_cart 10

  and then, on its third line, does this:

      var CALC = CALC_BY_PATH[path];
      if (!CALC) return;

  CALC_BY_PATH lists the four calculator pages and nothing else. /pages/quote-review
  is not in it, so the tracker installs and immediately returns on that page.

  That matters because steps 7 to 10 DO NOT HAPPEN ON THE CALCULATOR. Every one of
  them happens on /pages/quote-review:

      quote_saved       the customer is looking at their saved quote
      details_opened    they clicked "Continue to Payment" and the details modal opened
      enquiry_submitted they submitted that form
      add_to_cart       quote-review.js posted the cart line and sent them to /checkout

  So the funnel could only ever report steps 1 to 6, and its exit event - which carries
  the furthest step reached, the whole point of the design - topped out at 6 for glass,
  blade and PerfMesh. Read literally it says "everybody abandons at the price", which
  is precisely the question Pete needs answered and precisely the wrong answer.

  The one exception is why this was not obvious: flat top and PerfMesh bind their
  "Add to Cart" button to the calculator's own addToCart(), so THEY fire step 10 while
  still on the calculator page. Glass binds the same button to proceedToQuotePage(),
  which only saves the quote and redirects. That is why D1 held add_to_cart rows for
  flattop and none at all for glass - a binding difference, not customer behaviour.

  WHAT THIS FILE DOES. Runs only on /pages/quote-review, works out which calculator
  the quote came from, and fires steps 7 to 10 into the same worker endpoint, with the
  same event shape and the same visitor/session ids. cf_vid lives in localStorage and
  cf_sid in sessionStorage, both of which survive the redirect from the calculator, so
  the rows join up into one session without any extra plumbing.

  WHY NOT JUST ADD THE PATH TO cf-calc-analytics.js. Two reasons. Deriving the
  calculator on this page needs the saved quote, not the URL, so it is real extra
  logic rather than one map entry. And that file is owned by another workstream; a
  separate asset cannot regress it. If the two are ever merged, this header is the
  reason the merge has to keep the quote-derived CALC.

  Both files guard their own event names, so nothing is double counted.
*/
(function () {
  'use strict';

  if (window.__cfCalcAnalyticsReview) return;
  window.__cfCalcAnalyticsReview = true;

  var path = (location.pathname || '/').replace(/\/+$/, '') || '/';
  if (path !== '/pages/quote-review') return;

  var WORKER_URL = 'https://clickfencing-crm-api.ppfencing2.workers.dev/calc-event';

  /* Same ladder as cf-calc-analytics.js. Only the tail is used here. */
  var STEPS = {
    view: 1, start: 2, length_entered: 3, section_added: 4, gate_added: 5,
    price_shown: 6, quote_saved: 7, details_opened: 8, enquiry_submitted: 9,
    add_to_cart: 10
  };

  /* localStorage prefix -> the calc name already used in D1. "ullc" is glass. */
  var CALC_BY_PREFIX = {
    ullc: 'glass', blade: 'blade', flattop: 'flattop',
    perfmesh: 'perfmesh', batten: 'batten'
  };

  var startedAt = Date.now();
  /* Landing here with a quote means a price was shown, so step 6 is the floor. */
  var furthest = 6;
  var fired = {};
  var queue = [];
  var flushTimer = null;

  function safe(fn) {
    return function () {
      try { return fn.apply(this, arguments); }
      catch (err) {
        if (window.console && console.debug) console.debug('[cf-calc-analytics-review]', err);
      }
    };
  }

  function uid() {
    try { if (window.crypto && crypto.randomUUID) return crypto.randomUUID(); } catch (e) {}
    return 'x' + Math.random().toString(36).slice(2) + Date.now().toString(36);
  }

  function stored(store, key) {
    try {
      var v = store.getItem(key);
      if (!v) { v = uid(); store.setItem(key, v); }
      return v;
    } catch (e) { return 'nostore-' + uid(); }
  }

  var visitorId = stored(window.localStorage, 'cf_vid');
  var sessionId = stored(window.sessionStorage, 'cf_sid');

  function device() {
    var w = window.innerWidth || 0;
    return w < 768 ? 'mobile' : (w < 1024 ? 'tablet' : 'desktop');
  }

  /* Which calculator produced the quote on screen? The URL cannot say, so read the
     saved quotes and take the most recently generated one. Mirrors how
     cf-real-line-items.js picks the quote it bills. */
  function detectCalc() {
    var best = null;
    for (var prefix in CALC_BY_PREFIX) {
      if (!Object.prototype.hasOwnProperty.call(CALC_BY_PREFIX, prefix)) continue;
      var arr = [];
      try { arr = JSON.parse(localStorage.getItem(prefix + '_quotes') || '[]') || []; }
      catch (e) { arr = []; }
      if (Object.prototype.toString.call(arr) !== '[object Array]') continue;
      for (var i = 0; i < arr.length; i++) {
        var q = arr[i];
        if (!q) continue;
        var when = Date.parse(q.generatedAt) || 0;
        if (!best || when > best.when) {
          best = { when: when, calc: CALC_BY_PREFIX[prefix], total: Number(q.grandTotal) || 0 };
        }
      }
    }
    return best;
  }

  var detected = detectCalc();
  var CALC = detected ? detected.calc : 'unknown';

  function flush(useBeacon) {
    if (!queue.length) return;
    var batch = queue.splice(0, queue.length);
    var body = JSON.stringify({ events: batch });
    try {
      if (useBeacon && navigator.sendBeacon) {
        navigator.sendBeacon(WORKER_URL, new Blob([body], { type: 'text/plain;charset=UTF-8' }));
      } else {
        fetch(WORKER_URL, {
          method: 'POST', body: body, keepalive: true,
          headers: { 'Content-Type': 'text/plain;charset=UTF-8' }
        }).catch(function () {});
      }
    } catch (e) {}
  }

  var track = safe(function (name, detail) {
    var step = STEPS[name] || null;
    if (name !== 'exit') {
      if (fired[name]) return;
      fired[name] = true;
      if (step && step > furthest) furthest = step;
    }
    var evt = {
      visitorId: visitorId, sessionId: sessionId, calc: CALC, event: name,
      step: name === 'exit' ? furthest : step,
      detail: detail === undefined ? null : detail,
      path: path, device: device(), msOnPage: Date.now() - startedAt
    };
    queue.push(evt);

    /* GA4 via dataLayer, never a direct gtag() call - Shopify loads the tag library
       async and a direct call races it and is dropped silently. */
    try {
      window.dataLayer = window.dataLayer || [];
      var send = typeof window.gtag === 'function'
        ? window.gtag
        : function () { window.dataLayer.push(arguments); };
      send('event', 'cf_calc_' + name, {
        cf_calculator: CALC,
        cf_step: evt.step,
        cf_furthest_step: furthest,
        cf_seconds_on_page: Math.round(evt.msOnPage / 1000),
        cf_detail: typeof detail === 'object' ? JSON.stringify(detail) : detail
      });
    } catch (e) {}

    if (name === 'exit' || name === 'enquiry_submitted' || name === 'add_to_cart') {
      flush(name === 'exit');
    } else {
      clearTimeout(flushTimer);
      flushTimer = setTimeout(function () { flush(false); }, 2000);
    }
  });

  /* Step 7. They have a quote and they are looking at it. */
  if (detected) track('quote_saved', { total: detected.total });

  /* Step 8. "Continue to Payment" opens the details modal. Matched by id first, then
     by label, so a copy change does not silently stop the tracking. */
  document.addEventListener('click', safe(function (e) {
    var el = e.target && e.target.closest ? e.target.closest('button, a, [role="button"]') : null;
    if (!el) return;
    var label = (el.innerText || el.textContent || '').trim().toLowerCase().slice(0, 60);
    if (el.id === 'qr-enquire-btn' || /continue to payment|continue to checkout|your details/.test(label)) {
      track('details_opened', { label: label || el.id });
    }
    if (el.id === 'cf-name-plan-btn' || /name this plan/.test(label)) {
      track('quote_saved', { via: 'name-plan' });
    }
  }), true);

  /* Step 9. The details form submitting is the enquiry. */
  document.addEventListener('submit', safe(function (e) {
    var f = e.target;
    if (f && (f.id === 'qr-enquire-form' || (f.querySelector && f.querySelector('#qr-f-email')))) {
      track('enquiry_submitted', { form: f.id || 'qr-enquire-form' });
    }
  }), true);

  /* Step 10. quote-review.js posts the cart line then redirects to /checkout, so the
     request is the only reliable signal - the redirect can beat any click handler. */
  try {
    var origFetch = window.fetch;
    window.fetch = function (input) {
      var url = '';
      try { url = typeof input === 'string' ? input : (input && input.url) || ''; } catch (e) {}
      if (/\/cart\/add/.test(url)) track('add_to_cart');
      if (/\/enquiry\b/.test(url)) track('enquiry_submitted');
      return origFetch.apply(this, arguments);
    };
  } catch (e) {}

  var exited = false;
  var onExit = safe(function () {
    if (exited) return;
    exited = true;
    track('exit');
  });
  window.addEventListener('pagehide', onExit);
  window.addEventListener('beforeunload', onExit);
  document.addEventListener('visibilitychange', safe(function () {
    if (document.visibilityState === 'hidden') flush(true);
  }));

  if (window.console && console.log) {
    console.log('[cf-calc-analytics-review] installed for "' + CALC + '"');
  }
})();
